import React, { useEffect, useState } from 'react';
import {
  Box, Container, Heading, Text, Button, VStack, HStack, SimpleGrid, Icon,
  Flex, Badge, List, ListItem, ListIcon, Accordion, AccordionItem,
  AccordionButton, AccordionPanel, AccordionIcon, Image,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Textarea, InputGroup, Input, InputRightElement, IconButton,
  useDisclosure, useToast
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { FiCheck, FiTruck, FiSmartphone, FiClock, FiDollarSign, FiShield,
  FiBarChart2, FiMail, FiArrowRight, FiMessageCircle,
  FiGift, FiTarget, FiZap, FiStar, FiMic, FiMenu, FiX } from 'react-icons/fi';
import ChatWidget from '../Components/Chat/ChatWidget';
import ProductTour from '../Components/ProductTour/ProductTour';
import POSKitSection, { POS_KIT_PRICE } from '../Components/ProductTour/POSKitSection';

// ── FAQ Data (used for rendering AND for JSON-LD structured data) ──────────
const FAQ_DATA = [
  { q: 'What does this do at the counter?', a: 'Staff weigh the bag, charge by the pound or by the bag, print a tag, follow it through wash, dry, and fold, and text the customer when it is ready. People also search for this as wash and fold POS. Wash-Dry-Fold POS is a different company. We are not them. A comparison is at smartlaundrybasket.ai/wdf-pos.' },
  { q: 'Is this the register for wash and fold?', a: 'Yes. It is the counter screen for drop-off, pickup, and delivery. It opens in Chrome on a phone, tablet, or computer. It can read a scale, print bag tags, and take cards through your own Stripe account. Stripe is the card company. The money goes to your bank. It is free under $1,000 in a month and never more than $149 a month.' },
  { q: 'Is it really free to start?', a: 'Yes. No credit card and no contract. It stays free until a month of sales passes $1,000, then it is $49 a month. If you want a copy to run on your own computer, email info@smartlaundrybasket.ai.' },
  { q: 'How much does it cost?', a: 'The monthly price follows your sales: under $1,000 is free, $1,000 to $5,000 is $49, $5,000 to $10,000 is $74, $10,000 to $15,000 is $99, $15,000 to $25,000 is $124, and $25,000 or more is $149. You never pay more than $149 a month. No credit card to start, no contract, and card payments go to your bank. AI garment tracking is separate, at 1 cent per piece, only if you add it.' },
  { q: 'Do you take a cut of each order?', a: 'No. Card payments go to your bank through your own Stripe account. Our fee is a flat amount for the month, based on your sales: free under $1,000, then $49, and never more than $149. We do not take a percent of the order.' },
  { q: 'What kind of businesses can use this?', a: 'Two kinds. You already run a laundromat and want the counter, pickup and delivery, and the texts. Or you are starting an independent wash and fold service: you pick up someone’s clothes, wash and fold them, and bring them back, often beside another job and often without a store of your own. People also search for this as a gig worker, a fluff and fold side hustle, or a laundry route. Both use this wash and fold software for $0. You set your own prices. Laundromat owners call the order screen a POS. The monthly price is on the price table, and it never goes above $149. Both can turn on AI garment tracking, at 1 cent a piece, when they want a photo of every garment.' },
  { q: 'What does POS mean?', a: 'POS means point of sale. It is the register: the screen where you take the order, weigh the bag, and get paid. Laundromat owners search for a POS. Gig workers usually say software, or an app. This is that screen, on a phone, a tablet, or a computer, for $0 to start.' },
  { q: 'Is there wash and fold software for gig workers?', a: 'Yes. Smart Laundry Basket is a wash and fold POS for independent operators and gig workers who run pickup and delivery, also called a fluff and fold side hustle or a laundry route. It opens in Chrome on a phone. You set your prices, your hours, and the neighborhoods you pick up. Customers book on a page with your name, or they text PICKUP. The software is $0 until wash and fold sales pass $1,000 in a month. There is no contract and no percent taken from the order. Card payments go to your bank through your own Stripe account.' },
  { q: 'Can I start a wash and fold side hustle without a store?', a: 'Yes. You pick up the bag, wash and fold it, and bring it back. Many gig workers wash at home or at a laundromat they do not own. You use the phone you already have: a booking page, a photo of the scale for the weight, a ready text, and a link the customer can open when you are close. If you open a store later, the same account becomes the counter.' },
  { q: 'Do I have to pay for laundry software before the route makes money?', a: 'No. There is no credit card to start and no contract. The POS stays $0 until a month of wash and fold sales passes $1,000. From $1,000 to $5,000 it is $49 a month. It never goes above $149 a month. A tablet kit is optional, at $2,249 once, for a counter you want shipped to you. A route can start on a phone you already have.' },
  { q: 'Does this take a percent of each wash and fold order?', a: 'No. Gig workers and store owners pay the same flat monthly price, based on sales: free under $1,000, then $49, and never more than $149. Card payments go to your bank. We do not take a percent of the order, and customers book you on your page. Your name, your prices, and your service area stay yours.' },
  { q: 'What is a wash and fold route?', a: 'A wash and fold route is pickup and delivery laundry run by an independent operator. The customer leaves a bag, you collect it, wash and fold it, and return it. People also call this a fluff and fold side hustle, a laundry gig, or a wash and fold business from a car and a phone. Smart Laundry Basket is the POS for that route: booking, weight, price, texts, repeating weekly pickups, and one invoice when the customer is an Airbnb host, a gym, or a salon.' },
  { q: 'How do the reminder texts work?', a: 'You start from messages we already wrote: welcome, please leave a review, we miss you, and a thank-you after so many orders. You can also text new customers, people who have not come in, and regulars. You write the words and the discount. Each location can turn texts on or off. If a customer asked for email only, they get email.' },
  { q: 'What if someone starts a pickup and does not finish?', a: 'They get a text with a discount code. You choose how long to wait, what the text says, and the discount.' },
  { q: 'How does send-a-friend work?', a: 'Each customer gets their own code. When a friend orders with that code, both get credit on a later order. You set the dollar amounts, when the credit expires, and a monthly limit. You do that on the settings screen.' },
  { q: 'Can I run this on my own computer?', a: 'Yes. You can use it and change it for your own laundry. You may not sell it as a service to other laundries. Email info@smartlaundrybasket.ai and ask for a copy.' },
  { q: 'Can I start on my own computer and move to your hosting later?', a: 'Yes. Sign up for the hosted plan and we move your customers and orders over. You can also leave later and take your data with you.' },
  { q: 'Do I need special equipment?', a: 'No. The counter screen opens in Chrome on a phone, tablet, or computer you already have. Cards can be taken on a normal Stripe card reader. If you want the whole counter shipped to you, the kit is $2,249: tablet, stand, receipt printer, label printer, and cash drawer. We set it up, and shipping is included.' },
  { q: 'What comes in the $2,249 counter kit?', a: 'A tablet on a stand, a receipt printer, a label printer for bag tags, and a cash drawer that opens on a cash sale. We set it up for your laundry and ship it. You plug it in and take the next bag. You pay $2,249 once. The monthly price does not change.' },
  { q: 'Does it print receipts and bag tags?', a: 'Yes. The counter screen prints the customer receipt and the bag tag. The tag shows the order number, the customer, the due date, and a code staff can scan with a phone to open the order. On a cash sale the drawer opens, and every open is written down.' },
  { q: 'Can my staff work from a phone instead of the counter?', a: 'Yes. Every bag tag has a code. Staff scan it with any phone, enter their PIN, and get a checklist: write the weight, photograph what came in, then washing, drying, and folding, then mark it done. Each step records who did it. The customer is texted when the order is ready.' },
  { q: 'Does it connect to a scale?', a: 'Yes. Plug the scale into the computer or tablet with a cord and the pounds fill in. You do not retype them. A phone can also read a photo of the scale display. With no scale, staff type the weight. If one order has a flat-price bag and clothes charged by the pound, staff mark the flat-price bags. Only the rest is charged by weight.' },
  { q: 'How do staff find a finished order when the customer arrives?', a: 'With rack slots. When staff finish an order (or when bags come in), they note where it is kept — for example "B-12" — and the rack shows on the order board, in the order details, and in big letters when the bag tag is scanned. For walk-in orders, the same screen shows any balance still due and a one-tap "Mark Picked Up," and pickup is blocked until the order is paid.' },
  { q: 'Can I bill salons, gyms, and Airbnb hosts?', a: 'Yes. One person gets the bill, and they can have as many locations as they need. Each location is picked up as usual. At the end of the day they get one invoice, split by location, with each address on it. The invoice goes out through your Stripe account. You set how many days they have to pay, and you can text or email a reminder in one tap. One phone number can cover several of their locations. The customer picks which one.' },
  { q: 'Can I sell a monthly laundry plan?', a: 'Yes. For example, a set number of pounds each month for one price. Sign them up at the counter or let them sign up on your site. The pounds left go down as you weigh orders. You can also set a repeating weekly pickup, or a bag plan they pay ahead for.' },
  { q: 'Does it plan the driver route?', a: 'Yes. The day’s pickups and deliveries are grouped into a route for each driver. The driver sees the stops on their phone, can take an unassigned pickup with one tap, and takes a photo at pickup and at delivery. The customer gets a text with a link when the driver is getting close. A bag is not sent out until it is paid, or the customer has an account.' },
  { q: 'Can my staff clock in and out?', a: 'Yes. Staff clock in and out with a PIN on the counter tablet. You see hours and pay for each person for the pay period. You do not need a separate time clock. You also choose which staff can open reports and settings.' },
  { q: 'Can I give my staff daily checklists?', a: 'Yes. Create opening, closing, or cleaning checklists, pick the days they run, set a due time, and assign them to a role. Staff check items off by name during their shift, and managers see a history of what was done, when, and by whom — no paper clipboards.' },
  { q: 'Can staff take an order for a customer over the phone?', a: 'Yes. From "Place Order," staff look the customer up by phone number (or add them), pick services, schedule pickup and delivery, and choose how it\'s paid — cash, card, card on file, pay later, or invoice. The customer gets the same confirmation and tracking updates as if they had booked online.' },
  { q: 'Can I run coupons?', a: 'Yes. A code can take off a percent or a dollar amount. You can limit it to new customers or regulars, set a minimum order, how many times one person can use it, and the start and end dates. It can apply to the whole order or one service. Staff can use it at the counter, and customers can type it on your site. The same codes work in your texts and in send-a-friend credit.' },
  { q: 'Can I set a minimum weight?', a: 'Yes. Set a minimum, such as 10 lb, so a small bag is billed as 10 lb. Customers see that before they book. You can also set how long each service takes, a cutoff for same-day, and the delivery fee. The counter and the website use the same rules.' },
  { q: 'If a customer books a flat price per bag, can staff change it to price per pound?', a: 'No. When a customer books a per-bag price, staff can record the weight but can\'t swap the bag for a per-pound service, so the customer pays the price they agreed to. Orders can still mix flat-priced bags with per-pound items; staff mark which bags are flat-price at weigh-in and only the rest is billed by weight.' },
  { q: 'How are services organized at the counter?', a: 'They are in groups, such as wash and fold by the pound, by the bag, bedding, and dry cleaning, listed A to Z. Staff find the right one in a couple of taps. You change the list, the prices, and extras like hang-dry yourself.' },
  { q: 'How long does it take to get started?', a: 'Most laundromats take orders the same day. Sign up, add your prices, connect the card account, and the counter screen and your website are live. No contract and no credit card to start. If you order the counter kit, it arrives set up. You plug it in.' },
  { q: 'We have several locations. Do we set up each one?', a: 'Yes. Each location is set up on its own, and you can group them under one company. On the first location, choose "Create a company" and you get a code. Each extra location uses "Join a company" and that code. One login then shows every location together, and each location on its own.' },
  { q: 'How are orders kept separate by location?', a: 'Every order belongs to one location. That location’s counter screen shows only its own orders, customers, staff, and reports. The company login adds them up, with each location listed next to the company total.' },
  { q: 'Do we have to offer pickup, or can we only do walk-in drop-off?', a: 'Pickup is optional. You can run only walk-in drop-off. A walk-in order needs the clothes and the weight, not an address or a pickup time. If you turn delivery on for an order, it starts as your own driver. Uber or DoorDash is optional, on your own account with them.' },
  { q: 'Do I need a website to take payment?', a: 'No. At the counter you can take cash, a card, a card reader, or pay later. Cash and pay later need no card setup. Card and the card reader use your own card account (Stripe) for that location. The money goes to your bank. The website is only if you also want people to order online.' },
  { q: 'Can customers keep a prepaid balance on their account?', a: 'Yes. Staff can add funds to a customer\'s account at the counter — the customer gets an emailed receipt — and the balance is used to pay for their orders. Staff see the remaining balance on every order, and corrections are logged.' },
  { q: 'Is there a chat on my website?', a: 'Yes. It answers hours, prices, and whether you pick up their zip, using the hours and prices you entered. If they need a person, the message shows up on the counter screen.' },
  { q: 'How do the automatic texts work?', a: 'You set the rules. A new customer can get a welcome text. Someone who started a pickup and did not finish gets a code after the wait you choose. Someone with no order for the number of days you set gets a "we miss you" text. After an order is done, you can ask for a review. You write the messages and choose text, email, or both.' },
  { q: 'Can I change the wording of the texts and emails?', a: 'Yes. You can rewrite each one: order confirmed, we have your clothes, it is ready, the driver is on the way. The customer’s name, order number, and pickup time fill in on their own. Turn any message off. Customers choose text, email, or both, and that choice is followed.' },
  { q: 'What do customers see about their price and payment?', a: 'Everything spelled out. The tracking page lists each item with its unit price — for example "12 lb @ $1.95/lb" — notes when a minimum weight applies, and shows a plain payment status such as "Card on file," "Payment due," or "Payment needed." If a card is declined, the customer gets a one-tap link to update it.' },
  { q: 'Can I charge a delivery fee by distance?', a: 'Yes, for each location. No fee, one flat fee, or a fee by miles. For example, free within 10 miles, a flat fee out to 20, then a per-mile charge after that. The customer sees the fee before they confirm.' },
  { q: 'Can I offer extras like hang-dry or hypoallergenic detergent?', a: 'Yes. Each location can add extras such as hang-dry, an extra rinse, fabric softener, or hypoallergenic detergent. Charge per piece or per pound. Customers pick them while they order. Per-pound extras are priced after you weigh the bag.' },
  { q: 'Can customers leave a tip?', a: 'Yes, if you turn tips on. They can tip a percent or a dollar amount when they order, on a repeating order, or on an order that is not finished yet. The tip goes to your bank with the payment.' },
  { q: 'Can it tell my other tools when an order changes?', a: 'Yes. On the Integrations page you can send a notice when an order is created, changes step, is finished, is paid, or is cancelled. That notice can go to Zapier, Make, or a system you already use. Each notice is signed so you can tell it came from us. If it does not go through, we try again.' },
  { q: 'How much of my website can I change myself?', a: 'On the settings screen, with no code: your logo, color, a promo on the homepage, the service list and prices, your hours, and the headline. If you only do pickup and delivery, you can hide the walk-in parts.' },
  { q: 'What if someone outside my delivery area tries to book?', a: 'We save where they are. You can also put a "bring service to my area" form on your website. You see the list and decide where to add next.' },
  { q: 'What is AI garment tracking?', a: 'It is optional for a laundromat you already run and for a gig worker who picks up, washes, and delivers. Customers hand over personal clothes and expect every piece back. It keeps a picture from before the wash and again at folding. Staff keep working the way they already do. If what was folded does not match what came in, the employee is told before the order leaves. If a customer says a shirt is missing, you have the picture. It is 1 cent per piece. Turn it on with this POS, or add it to the laundry software you already run.' },
  { q: 'Can I add AI garment tracking to the laundry software I already use?', a: 'Yes. Keep what you have and add AI garment tracking for 1 cent per piece. We help you set that up. It works with the printers and the counter devices you already use.' },
  { q: 'Can each service have its own turnaround?', a: 'Yes. You set the hours. Same-day is 0, next-day is 24, standard is 48. The wash-and-fold due date follows the slowest wash-and-fold service in the order, so a standard-price item cannot be promised back faster than you offer. Leave it blank to keep the usual next-day minimum. Dry cleaning marked Separate return uses the cleaner’s days and does not move the wash-and-fold date.' },
  { q: 'Can customers add dry cleaning to a wash and fold pickup?', a: 'Yes, if you turn it on. You set a price for each piece, such as a dress shirt or a coat. The customer adds those pieces to the same wash-and-fold order. Your driver still makes one pickup. It is one order and one bill. You keep the difference between what the cleaner charges you and the price on your menu.' },
  { q: 'Can dry cleaning come back on a different day than the wash and fold?', a: 'Yes. Wash and fold comes back on your usual turnaround. Dry cleaning comes back on the cleaner’s schedule. You set the weekdays the cleaner accepts work and how many days they need. The customer sees both dates before they confirm, and they can only move the dry-cleaning date later. A shirt that takes several days does not hold the rest of the bag.' },
  { q: 'How do I turn dry cleaning on?', a: 'On Services, open Dry cleaning schedule and check the days your cleaner accepts work, plus how many days they need. On the category, check Separate return, and put a price on each piece. When the clothes come back, open the order and tap Back from the cleaner. The driver gets a second stop on that later date, even if the wash and fold was already delivered.' },
  { q: 'What sales and tax reports are included?', a: 'The Dashboard is the daily picture: today’s revenue, the month, orders in progress, which services are selling, repeat customers next to new ones, and each employee’s orders and tips. Reports covers any date range: booked sales, what is already collected, what is still pay-later, sales tax, a State Comptroller view, tips, and expenses you enter. All Reports also includes daily sales, sales by service, sales by order type, payments by method, unpaid orders, cancelled orders, new customers, recurring pickups, and promo codes. Any report can be downloaded or emailed to you every day, every Monday, or on the 1st of the month.' },
  { q: 'Can I see how each employee is doing?', a: 'Yes. Team Performance shows hours, orders taken in, washed, folded, and handed off, pounds folded per hour, and tips. Time Cards is the clock-in and clock-out list for payroll. The Dashboard also lists each employee’s orders and tips for the period you pick.' },
  { q: 'What if a same-day order comes in too late?', a: 'You set how many hours you need before closing to still promise same-day. If there is not enough time left, the customer is asked to drop off earlier or take it tomorrow. You do not promise a same-day job you cannot finish.' },
  { q: 'Can repeating pickups and bag plans have different discounts?', a: 'Yes. Two separate percents, for each location. One is for repeating pickups charged by the pound or by the piece. The other is for bag plans they pay ahead for. The first order and every later order use the same discount, including after you weigh the bag.' },
];

// Monthly platform fee by monthly revenue — must match AGREEMENT_TERMS in QuickStartPage/OnboardingPage.
const PRICING_TIERS = [
  { range: '$0 – $999', price: 'Free', highlight: true },
  { range: '$1k – $5k', price: '$49/mo' },
  { range: '$5k – $10k', price: '$74/mo' },
  { range: '$10k – $15k', price: '$99/mo' },
  { range: '$15k – $25k', price: '$124/mo' },
  { range: '$25k+', price: '$149/mo', note: 'the most you pay' },
];

// ── Live customer storefronts (social proof) ──────────────────────────────
// Real laundromats running on the platform. Each links to their live site.
const LIVE_CUSTOMERS = [
  {
    name: 'Fetch & Fold Concierge',
    location: 'Pickup & delivery wash and fold service',
    outcome: 'Customers book pickup and delivery on their own site.',
    url: 'https://book.fetchandfold.com',
    initials: 'FF',
    color: 'purple',
  },
  {
    name: 'Round Rock Laundry',
    location: 'Round Rock, TX',
    outcome: 'A laundromat taking wash and fold orders on its own site.',
    url: 'https://www.roundrocklaundry.com/1/site',
    initials: 'RR',
    color: 'blue',
  },
  {
    name: 'Spin & Shine Laundromat',
    location: 'Full-service laundromat',
    outcome: 'A full-service laundromat running orders on its own site.',
    url: 'https://www.spinandshinelaundromat.com/2/site',
    initials: 'SS',
    color: 'teal',
  },
  {
    name: 'Top Tier Coin Laundry',
    location: 'Coin laundry & full-service',
    outcome: 'Coin laundry with a full-service wash and fold side.',
    url: 'https://book.toptiercoinlaundry.com/',
    initials: 'TT',
    color: 'orange',
  },
];

const LIVE_CUSTOMER_LINE = (() => {
  const names = LIVE_CUSTOMERS.map((c) => c.name);
  if (names.length < 2) return names[0] || '';
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
})();

// JSON-LD FAQPage structured data for SEO
const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_DATA.map(item => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
};

const ProductWebsite = () => {
  // Generate a stable visitor ID for the chat widget
  const [visitorId] = useState(() => {
    const stored = localStorage.getItem('slb_prospect_id');
    if (stored) return stored;
    const id = `prospect-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('slb_prospect_id', id);
    return id;
  });

  useEffect(() => {
    document.title = 'Wash and fold POS, or start a wash and fold business for free | Smart Laundry Basket';
  }, []);

  // "Request Source Access" — mailto: links silently do nothing when the
  // visitor has no default mail client (common on desktop). Instead we open a
  // modal that shows the email, lets them copy it, and offers to open their
  // mail app as a best-effort fallback.
  const SOURCE_EMAIL = 'info@smartlaundrybasket.ai';
  const SOURCE_SUBJECT = 'Source code access request';
  const { isOpen: srcOpen, onOpen: openSource, onClose: closeSource } = useDisclosure();
  const { isOpen: menuOpen, onToggle: toggleMenu, onClose: closeMenu } = useDisclosure();
  const productNav = [
    ['#who-its-for', 'Choose your path'],
    ['#the-shift', 'One bag'],
    ['#pricing', 'Price'],
    ['#tour', 'See the screens'],
    ['#pos-kit', 'Counter kit'],
    ['#ai-tracking', 'AI garment tracking'],
    ['/slb/get-customers', 'Get customers'],
    ['#faq', 'FAQ'],
  ];
  const [srcMessage, setSrcMessage] = useState(
    "Hi, I'd like a copy of Smart Laundry Basket to run on my own computer for my laundry. Please tell me the next step."
  );
  const toast = useToast();

  const copySourceEmail = async () => {
    try {
      await navigator.clipboard.writeText(SOURCE_EMAIL);
      toast({ title: 'Email copied', description: SOURCE_EMAIL, status: 'success', duration: 2500, isClosable: true });
    } catch (e) {
      toast({ title: 'Copy failed', description: `Please copy manually: ${SOURCE_EMAIL}`, status: 'warning', duration: 4000, isClosable: true });
    }
  };

  const openMailApp = () => {
    const href = `mailto:${SOURCE_EMAIL}?subject=${encodeURIComponent(SOURCE_SUBJECT)}&body=${encodeURIComponent(srcMessage)}`;
    window.location.href = href;
  };

  return (
    <Box bg="white" minH="100vh">
      {/* SEO Head — JSON-LD FAQPage + meta */}
      <Helmet>
        <title>Wash and fold POS, or start a wash and fold business for free | Smart Laundry Basket</title>
        <meta name="description" content="Wash and fold software for laundromat owners and independent wash and fold services. Free until $1,000 in a month, then from $49, never more than $149. No contract. No percent of sales." />
        <meta name="keywords" content="WDF POS, wash and fold POS, wash dry fold POS, laundromat POS, wash dry fold software, drop-off laundry POS, WDF software, laundry pickup and delivery software, wash and fold side hustle, wash and fold gig, fluff and fold software, laundry route software, start a wash and fold business, independent laundry operator, dry cleaning pickup and delivery, laundromat sales reports, laundry employee time clock" />
        <link rel="canonical" href="https://smartlaundrybasket.ai" />
        <meta property="og:title" content="Wash and fold POS, or start a wash and fold business for free | Smart Laundry Basket" />
        <meta property="og:description" content="Free until $1,000 in a month. Then from $49. Never more than $149. For a laundromat or an independent wash and fold service." />
        <meta property="og:url" content="https://smartlaundrybasket.ai" />
        <script type="application/ld+json">
          {JSON.stringify(FAQ_JSON_LD)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Smart Laundry Basket',
            alternateName: ['wash and fold POS', 'wash dry fold POS software', 'laundromat POS', 'wash and fold software for gig workers'],
            applicationCategory: 'BusinessApplication',
            applicationSubCategory: 'Wash and fold point of sale',
            operatingSystem: 'Web',
            audience: [
              { '@type': 'BusinessAudience', audienceType: 'Laundromat owners with a drop-off counter' },
              { '@type': 'BusinessAudience', audienceType: 'People opening a wash and fold business' },
              { '@type': 'BusinessAudience', audienceType: 'Independent wash and fold services starting pickup and delivery without owning a laundromat' },
            ],
            offers: [
              { '@type': 'AggregateOffer', lowPrice: '0', highPrice: '149', priceCurrency: 'USD', description: 'WDF software — free under $1,000/month revenue, then $49–$149/month by revenue tier (capped at $149)' },
              { '@type': 'Offer', price: '0.01', priceCurrency: 'USD', description: 'AI garment tracking — 1 cent per garment' },
              { '@type': 'Offer', price: String(POS_KIT_PRICE), priceCurrency: 'USD', description: 'POS Kit — tablet, POS stand, receipt printer, label printer, and cash drawer; setup and shipping included (one-time)' },
            ],
            description: 'Wash and fold software for laundromat owners and for independent wash and fold services that run pickup and delivery without owning a store. Weigh a bag, tag it, and a text goes out when it is ready. Customers book on your page. Card payments go to your bank. Free under $1,000 in monthly revenue, then from $49/month, capped at $149/month. No contract and no percent of the order. AI garment tracking is 1 cent per garment. Wash-Dry-Fold POS (WDF POS) is a different company.',
            url: 'https://smartlaundrybasket.ai',
          })}
        </script>
      </Helmet>

      {/* Sticky Navbar */}
      <Box as="nav" position="sticky" top={0} zIndex={100} bg="white" borderBottom="1px solid" borderColor="gray.100" boxShadow="sm">
        <Container maxW="1200px">
          <Flex py={3} align="center" justify="space-between">
            <HStack spacing={2}>
              <Image src="/slb-logo.png" alt="Smart Laundry Basket" h={{ base: '40px', md: '52px' }} objectFit="contain" />
            </HStack>
            <HStack spacing={6} display={{ base: 'none', xl: 'flex' }}>
              {productNav.map(([href, label]) => (
                <Text key={href} as="a" href={href} fontSize="sm" color="gray.600" _hover={{ color: 'blue.500' }}>{label}</Text>
              ))}
            </HStack>
            <HStack spacing={2}>
              <Button as="a" href="/quickstart" size="sm" colorScheme="blue" borderRadius="full" px={6}>Start free</Button>
              <IconButton
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                icon={<Icon as={menuOpen ? FiX : FiMenu} boxSize={6} />}
                variant="ghost"
                display={{ base: 'inline-flex', xl: 'none' }}
                onClick={toggleMenu}
              />
            </HStack>
          </Flex>
          {menuOpen && (
            <VStack display={{ base: 'flex', xl: 'none' }} align="stretch" pb={3} spacing={0}>
              {productNav.map(([href, label]) => (
                <Text
                  key={href}
                  as="a"
                  href={href}
                  onClick={closeMenu}
                  py={3}
                  px={2}
                  fontWeight="600"
                  borderTop="1px solid"
                  borderColor="gray.100"
                  _hover={{ color: 'blue.500' }}
                >
                  {label}
                </Text>
              ))}
            </VStack>
          )}
        </Container>
      </Box>

      {/* Hero Section */}
      <Box position="relative" overflow="hidden" py={{ base: 14, md: 16 }}>
        <Box as="video" autoPlay muted loop playsInline
          position="absolute" top="0" left="0" w="100%" h="100%"
          objectFit="cover" zIndex="0">
          <source src="https://laundry-images-store-prod.s3.us-east-1.amazonaws.com/15380072_3840_2160_30fps.mp4" type="video/mp4" />
        </Box>
        <Box position="absolute" top="0" left="0" w="100%" h="100%"
          bg="linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.7) 100%)" zIndex="1" />

        <Container maxW="980px" textAlign="center" position="relative" zIndex="2">
          <VStack spacing={4}>
            <Heading as="h1" fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }} color="white" lineHeight="shorter" fontWeight="800" maxW="920px"
              textShadow="0 2px 30px rgba(0,0,0,0.5)">
              Wash and fold POS for your laundromat.
            </Heading>
            <Text fontSize={{ base: 'xl', md: '2xl' }} color="yellow.300" fontWeight="800" lineHeight="shorter" maxW="820px"
              textShadow="0 2px 20px rgba(0,0,0,0.45)">
              Software to start a wash and fold business for free.
            </Text>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="white" fontWeight="600"
              textShadow="0 1px 10px rgba(0,0,0,0.4)">
              Free while sales are under $1,000 a month. Then $49 a month.
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button as="a" href="#laundromat-pos" size="lg" colorScheme="blue" borderRadius="full" px={8} boxShadow="lg"
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}>
                Wash and fold POS
              </Button>
              <Button as="a" href="#gig-workers" size="lg" bg="white" color="blue.700" borderRadius="full" px={8}
                _hover={{ bg: 'gray.100' }}>
                Start for free
              </Button>
            </HStack>
            <Text fontSize="sm" color="whiteAlpha.900" fontWeight="600">
              No credit card. No contract. No percent of sales.
            </Text>
          </VStack>
        </Container>
      </Box>

      <Box bg="purple.50" borderBottom="1px solid" borderColor="purple.100" py={4}>
        <Container maxW="1000px">
          <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="center" gap={{ base: 2, md: 4 }} textAlign="center">
            <HStack spacing={2} color="purple.700">
              <Icon as={FiMic} />
              <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="wide">As featured on the Laundromat Resource Podcast</Text>
            </HStack>
            <Text fontSize="sm" color="gray.700">Episode 255: "The Next Evolution in the Laundromat Industry?" (Aug 2026)</Text>
            <Button as="a" size="xs" colorScheme="purple" borderRadius="full" target="_blank" rel="noopener noreferrer"
              href="https://www.buzzsprout.com/1055122/episodes/19636098-255-the-next-evolution-in-the-laundromat-industry-with-suresh-alluru">
              Listen
            </Button>
          </Flex>
        </Container>
      </Box>

      <Box id="who-its-for" py={{ base: 16, md: 20 }} bg="white">
        <Container maxW="1100px">
          <VStack spacing={3} textAlign="center" mb={10}>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>Choose how you operate.</Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box id="laundromat-pos" bg="blue.50" borderRadius="2xl" p={{ base: 6, md: 8 }} border="1px solid" borderColor="blue.100">
              <Badge colorScheme="blue" mb={3}>Laundromat</Badge>
              <Heading as="h3" fontSize="2xl" mb={3}>Wash and fold POS.</Heading>
              <Text color="gray.700" mb={4}>For a store doing wash and fold, or about to start it. Your washers stay. The coin or card machines stay.</Text>
              <List spacing={2} mb={6}>
                <ListItem fontSize="sm"><ListIcon as={FiCheck} color="green.500" />Take wash and fold orders at the counter.</ListItem>
                <ListItem fontSize="sm"><ListIcon as={FiCheck} color="green.500" />Add pickup and delivery.</ListItem>
                <ListItem fontSize="sm"><ListIcon as={FiCheck} color="green.500" />See who handled each order, and the day’s sales.</ListItem>
                <ListItem fontSize="sm"><ListIcon as={FiCheck} color="green.500" />Texts go out on their own. No percent of sales.</ListItem>
              </List>
              <VStack align="stretch" spacing={3}>
                <Button as="a" href="/quickstart" colorScheme="blue" borderRadius="full" rightIcon={<FiArrowRight />}>Set Up My Laundromat — Free</Button>
                <Button as="a" href="#wdf-software" variant="outline" colorScheme="blue" borderRadius="full">See the POS</Button>
              </VStack>
            </Box>
            <Box id="gig-workers" bg="orange.50" borderRadius="2xl" p={{ base: 6, md: 8 }} border="1px solid" borderColor="orange.100">
              <Badge colorScheme="orange" mb={3}>No store yet</Badge>
              <Heading as="h3" fontSize="2xl" mb={3}>Start a wash and fold business for free.</Heading>
              <Text color="gray.700" mb={4}>You do not need a laundromat. This software is how you take the first order. A phone is enough.</Text>
              <List spacing={2} mb={6}>
                <ListItem fontSize="sm"><ListIcon as={FiCheck} color="green.500" />Free until a month of sales passes $1,000.</ListItem>
                <ListItem fontSize="sm"><ListIcon as={FiCheck} color="green.500" />Customers book on your page. You do not build an app.</ListItem>
                <ListItem fontSize="sm"><ListIcon as={FiCheck} color="green.500" />Pickups and deliveries. Cards go to your bank.</ListItem>
                <ListItem fontSize="sm"><ListIcon as={FiCheck} color="green.500" />Texts go out on their own.</ListItem>
              </List>
              <VStack align="stretch" spacing={3}>
                <Button as="a" href="/quickstart" colorScheme="orange" borderRadius="full" rightIcon={<FiArrowRight />}>Start My Wash and Fold Business — Free</Button>
                <Button as="a" href="/slb/get-customers" variant="outline" colorScheme="orange" borderRadius="full">How to get customers</Button>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Box id="the-shift" py={{ base: 14, md: 20 }} bg="gray.50">
        <Container maxW="1100px">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">One bag</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>What happens to one bag</Heading>
            <Text color="gray.600" maxW="680px" fontSize="lg">
              Same steps at a store counter or from a phone.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={10}>
            {[
              ['Take the bag', 'At your counter, or on a pickup.'],
              ['Weigh and price', 'By the pound or by the bag. You set the price.'],
              ['Text when it is ready', 'A text goes out on its own.'],
              ['Get paid', 'The card payment goes to your bank.'],
            ].map(([title, body], index) => (
              <HStack key={title} align="start" bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={4} spacing={4}>
                <Flex w="36px" h="36px" borderRadius="full" bg="blue.500" color="white" align="center" justify="center" fontWeight="800" flexShrink={0}>
                  {index + 1}
                </Flex>
                <Box>
                  <Text fontWeight="700">{title}</Text>
                  <Text fontSize="sm" color="gray.600">{body}</Text>
                </Box>
              </HStack>
            ))}
          </SimpleGrid>
          <Text textAlign="center" fontSize="sm" color="gray.600" mb={8}>
            A phone is enough to start. A store can add a tablet, printers, and a cash drawer later. <Text as="a" href="#pos-kit" color="blue.600" fontWeight="700">Counter kit, ${POS_KIT_PRICE.toLocaleString()}, optional.</Text>
          </Text>
          <VStack spacing={3} textAlign="center">
            <Text fontSize="sm" color="gray.600">
              Running today at {LIVE_CUSTOMER_LINE}.
            </Text>
            <HStack spacing={3} pt={2}>
              <Button as="a" href="/quickstart" colorScheme="blue" borderRadius="full">Start free</Button>
              <Button as="a" href="#pricing" variant="outline" borderRadius="full">See the price</Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Pricing — counter software first, AI add-on kept separate */}
      <Box id="pricing" py={{ base: 16, md: 20 }} bg="blue.50">
        <Container maxW="1100px" textAlign="center">
          <VStack spacing={6}>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>$0 to start</Heading>
            <HStack spacing={3} justify="center" flexWrap="wrap">
              <Button as="a" href="/quickstart" colorScheme="blue" borderRadius="full" size="lg" px={8}>Set Up My Laundromat — Free</Button>
              <Button as="a" href="/quickstart" colorScheme="orange" borderRadius="full" size="lg" px={8}>Start My Wash and Fold Business — Free</Button>
            </HStack>

            <Box bg="white" borderRadius="2xl" boxShadow="sm" p={{ base: 5, md: 8 }} w="100%" maxW="820px" mt={4}>
              <Text fontWeight="700" fontSize="lg" mb={1}>The price follows your sales</Text>
              <Text color="gray.500" fontSize="sm" mb={5}>We look at what you sold that month. Never more than $149 a month.</Text>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3}>
                {PRICING_TIERS.map((tier) => (
                  <Box key={tier.range} p={3} borderRadius="lg" bg={tier.highlight ? 'green.50' : 'gray.50'}
                    border="1px solid" borderColor={tier.highlight ? 'green.200' : 'gray.100'}>
                    <Text fontSize="xs" color="gray.500" fontWeight="600">{tier.range}</Text>
                    <Text fontSize="xl" fontWeight="800" color={tier.highlight ? 'green.600' : 'blue.600'}>{tier.price}</Text>
                    {tier.note && <Text fontSize="xs" color="gray.500">{tier.note}</Text>}
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            <Text fontSize="sm" color="gray.500" pt={2}>
              A phone is enough to start a wash and fold business. A laundromat can add a tablet, printers, and a cash drawer later. <Text as="a" href="#pos-kit" color="blue.600" fontWeight="600">Counter kit, ${POS_KIT_PRICE.toLocaleString()}, optional.</Text>
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Trusted By — live customer storefronts for social proof */}
      <Box id="customers" py={{ base: 16, md: 20 }} bg="gray.50">
        <Container maxW="1200px">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Badge colorScheme="green" px={3} py={1} borderRadius="full">Trusted By Real Businesses</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>Wash and fold businesses taking orders on it now.</Heading>
            <Text color="gray.600" maxW="650px" fontSize="lg">
              Open their ordering sites. These are live businesses, not a demo.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {LIVE_CUSTOMERS.map((cust) => (
              <Box
                key={cust.url}
                as="a"
                href={cust.url}
                {...(cust.sameTab ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                p={8}
                borderRadius="2xl"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
                transition="all 0.3s"
                _hover={{ boxShadow: 'lg', transform: 'translateY(-4px)', borderColor: `${cust.color}.200` }}
                display="block"
              >
                <VStack spacing={4} align="center" textAlign="center">
                  <Flex
                    bg={`${cust.color}.50`}
                    color={`${cust.color}.600`}
                    w="64px"
                    h="64px"
                    borderRadius="full"
                    align="center"
                    justify="center"
                    fontWeight="800"
                    fontSize="xl"
                  >
                    {cust.initials}
                  </Flex>
                  <VStack spacing={1}>
                    <Text fontWeight="700" fontSize="lg">{cust.name}</Text>
                    <Text color="gray.500" fontSize="sm">{cust.location}</Text>
                    {cust.outcome && <Text color="gray.600" fontSize="sm">{cust.outcome}</Text>}
                  </VStack>
                  <HStack spacing={1} color={`${cust.color}.500`} fontSize="sm" fontWeight="600">
                    <Text>{cust.cta || 'Open their ordering site'}</Text>
                    <Icon as={FiArrowRight} />
                  </HStack>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <ProductTour />

      <Box id="what-you-get" py={{ base: 14, md: 16 }} bg="white">
        <Container maxW="1100px">
          <VStack spacing={3} textAlign="center" mb={10}>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>What the software covers</Heading>
            <Text color="gray.600" fontSize="lg" maxW="640px">
              This is the whole product.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box bg="gray.50" borderRadius="2xl" p={7} border="1px solid" borderColor="gray.100">
              <Icon as={FiTruck} boxSize={7} color="blue.500" mb={3} />
              <Heading as="h3" fontSize="xl" mb={2}>Run the operation</Heading>
              <Text color="gray.600" fontSize="sm">A laundromat runs the counter and the staff. A new business runs pickups from a phone.</Text>
            </Box>
            <Box bg="gray.50" borderRadius="2xl" p={7} border="1px solid" borderColor="gray.100">
              <Icon as={FiTarget} boxSize={7} color="orange.500" mb={3} />
              <Heading as="h3" fontSize="xl" mb={2}>Grow revenue</Heading>
              <Text color="gray.600" fontSize="sm">Online booking, pickup and delivery, memberships, referrals, and texts that bring people back.</Text>
            </Box>
            <Box bg="gray.50" borderRadius="2xl" p={7} border="1px solid" borderColor="gray.100">
              <Icon as={FiShield} boxSize={7} color="purple.500" mb={3} />
              <Heading as="h3" fontSize="xl" mb={2}>Protect the business</Heading>
              <Text color="gray.600" fontSize="sm">AI garment tracking, photos, employee accountability, and sales for the day.</Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Box py={{ base: 10, md: 12 }} bg="orange.50">
        <Container maxW="760px" textAlign="center">
          <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }}>Starting a wash and fold business?</Heading>
          <Text color="gray.700" mt={3} fontSize="lg">
            You can stop here. A phone, your prices, and a booking page are enough. Free while sales are under $1,000 a month. The counter, printers, and cash drawer below are for a laundromat.
          </Text>
          <Button as="a" href="/quickstart" mt={5} colorScheme="orange" borderRadius="full" size="lg">Start My Wash and Fold Business — Free</Button>
        </Container>
      </Box>

      <Box id="wdf-software" py={{ base: 16, md: 20 }}>
        <Container maxW="1200px">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">For a laundromat</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>The counter, pickup, and the ready text.</Heading>
            <Text color="gray.600" maxW="680px" fontSize="lg">
              One screen for the bag at the counter, the pickups for the day, and the text that says it is ready. A store and a new wash and fold service both get this.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {[
              { icon: FiSmartphone, title: 'The customer site', desc: 'Customers book wash and fold, pay, and see the bag move from picked up to ready. Your name, prices, and hours.', color: 'orange' },
              { icon: FiDollarSign, title: 'The counter', desc: 'Walk-in checkout. Tap the service, enter the weight, and take cash, a card, or a card reader. The cash drawer opens on cash.', color: 'green' },
              { icon: FiTruck, title: 'The pickup route', desc: 'Text PICKUP or book online. Drivers get the day’s stops. The customer gets a text when the driver is close.', color: 'blue' },
              { icon: FiClock, title: 'Washer, dryer, rack', desc: 'Move the bag from the counter to the washer, the dryer, the fold table, and a rack such as B-12. Staff enter their PIN at each move.', color: 'purple' },
              { icon: FiMail, title: 'The ready text', desc: 'The customer is texted when the bag is on the rack. Also included: a text if they have not come in, a code if they started a pickup and stopped, and credit when they send a friend.', color: 'pink' },
              { icon: FiBarChart2, title: 'Pounds and dollars', desc: 'Daily sales, sales tax, tips, and who washed, folded, and handed the bag off. Download a report or have it emailed every Monday or on the 1st.', color: 'teal' },
            ].map((feature) => (
              <Box key={feature.title} p={6} borderRadius="xl" border="1px solid" borderColor="gray.100"
                _hover={{ boxShadow: 'lg', transform: 'translateY(-4px)' }} transition="all 0.3s">
                <Box bg={`${feature.color}.50`} w="50px" h="50px" borderRadius="lg" display="flex" alignItems="center" justifyContent="center" mb={4}>
                  <Icon as={feature.icon} boxSize={6} color={`${feature.color}.500`} />
                </Box>
                <Text fontWeight="700" fontSize="lg" mb={2}>{feature.title}</Text>
                <Text color="gray.600" fontSize="sm">{feature.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>

          <VStack spacing={2} mt={10}>
            <Heading fontSize={{ base: '3xl', md: '4xl' }} fontWeight="900" color="blue.600">Free until $1,000. Then from $49.</Heading>
            <Text fontSize="sm" color="gray.500">Never more than $149. No credit card. No contract. No percent of sales.</Text>
            <HStack spacing={3} pt={2} flexWrap="wrap" justify="center">
              <Button as="a" href="/quickstart" size="lg" colorScheme="blue" borderRadius="full" px={8}>Set Up My Laundromat — Free</Button>
              <Button as="a" href="/quickstart" size="lg" colorScheme="orange" borderRadius="full" px={8}>Start My Wash and Fold Business — Free</Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Box id="dry-cleaning" py={{ base: 16, md: 20 }} bg="gray.50">
        <Container maxW="1100px">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">On the same order</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>Dry cleaning on the same pickup.</Heading>
            <Text color="gray.600" maxW="720px" fontSize="lg">
              A customer who already sends a bag can add shirts, coats, or dresses. Wash and fold comes back on your usual day. Dry cleaning comes back when the cleaner is done. The dashboard and the reports show what sold and who produced it.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mb={8}>
            <Box p={8} borderRadius="2xl" bg="white" border="1px solid" borderColor="gray.200">
              <Heading as="h3" fontSize="xl" mb={3}>Same pickup, two return dates</Heading>
              <Text color="gray.600" mb={4}>
                You set the piece prices. The customer adds them to the wash-and-fold order. The driver still makes one stop. It is one order and one bill.
              </Text>
              <List spacing={2}>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />You set the days the cleaner accepts work, and how many days they need</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />The customer sees both dates before they confirm</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />They can only move the dry-cleaning date later</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Tap Back from the cleaner, and the driver gets a second stop on that date</ListItem>
              </List>
            </Box>
            <Box p={8} borderRadius="2xl" bg="white" border="1px solid" borderColor="gray.200">
              <Heading as="h3" fontSize="xl" mb={3}>Dashboard, reports, and the team</Heading>
              <Text color="gray.600" mb={4}>
                Today’s revenue, the month, which services sold, and each employee’s orders and tips are on the Dashboard. Reports is the longer statement.
              </Text>
              <List spacing={2}>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Booked sales, money already collected, and what is still pay-later</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Sales tax, tips, and expenses you enter</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Team Performance: hours, washed, folded, handed off, and pounds per hour</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Email any report every day, every Monday, or on the 1st</ListItem>
              </List>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Marketing & Engagement Section — NEW */}
      <Box id="marketing" py={{ base: 16, md: 20 }} bg="gray.50">
        <Container maxW="1200px">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Badge colorScheme="purple" px={3} py={1} borderRadius="full">Included texts</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>Texts that bring the next bag in.</Heading>
            <Text color="gray.600" maxW="680px" fontSize="lg">
              These are in the monthly price. A customer who has not dropped off in a month gets a "we miss you" text. Someone who started a pickup and stopped gets a code to finish it.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {/* SMS Marketing */}
            <Box p={8} borderRadius="2xl" bg="white" border="1px solid" borderColor="gray.200" boxShadow="sm">
              <HStack mb={4}>
                <Box bg="purple.50" p={3} borderRadius="lg">
                  <Icon as={FiMail} boxSize={6} color="purple.500" />
                </Box>
                <Heading as="h3" fontSize="xl">Texts to customers who went quiet</Heading>
              </HStack>
              <Text color="gray.600" mb={4}>
                You write the message. It goes to new customers, regulars, or people who have not ordered in the number of days you set.
              </Text>
              <List spacing={2}>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Uses the customer’s first name</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Attach a code, such as $5 off the next bag</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Turn texts on or off per location</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Holiday wording you can edit</ListItem>
              </List>
            </Box>

            {/* Abandoned Cart Recovery */}
            <Box p={8} borderRadius="2xl" bg="white" border="1px solid" borderColor="gray.200" boxShadow="sm">
              <HStack mb={4}>
                <Box bg="orange.50" p={3} borderRadius="lg">
                  <Icon as={FiTarget} boxSize={6} color="orange.500" />
                </Box>
                <Heading as="h3" fontSize="xl">A pickup that was not finished</Heading>
              </HStack>
              <Text color="gray.600" mb={4}>
                A customer starts booking a pickup and leaves. After the wait you set, they get a text with a code to finish the booking.
              </Text>
              <List spacing={2}>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />You set the wait, the message, and the discount</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />Works for a first booking and a returning customer</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />You see which texts turned into an order</ListItem>
              </List>
            </Box>

            {/* Customer Engagement Engine */}
            <Box p={8} borderRadius="2xl" bg="white" border="1px solid" borderColor="gray.200" boxShadow="sm">
              <HStack mb={4}>
                <Box bg="teal.50" p={3} borderRadius="lg">
                  <Icon as={FiZap} boxSize={6} color="teal.500" />
                </Box>
                <Heading as="h3" fontSize="xl">A text when they stop coming</Heading>
              </HStack>
              <Text color="gray.600" mb={4}>
                Set a gap, such as 30 days with no bag. Those customers get a "we miss you" text. A weekly or monthly reminder uses the same setup.
              </Text>
              <List spacing={2}>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />You choose the gap and the wording</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />New, regular, and quiet customers can get different texts</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />It sends after you turn it on</ListItem>
              </List>
            </Box>

            {/* Referral Program */}
            <Box p={8} borderRadius="2xl" bg="white" border="1px solid" borderColor="gray.200" boxShadow="sm">
              <HStack mb={4}>
                <Box bg="green.50" p={3} borderRadius="lg">
                  <Icon as={FiGift} boxSize={6} color="green.500" />
                </Box>
                <Heading as="h3" fontSize="xl">A customer sends a friend</Heading>
              </HStack>
              <Text color="gray.600" mb={4}>
                Each customer has a code. When a friend places an order, both get credit toward the next bag. You set the amounts.
              </Text>
              <List spacing={2}>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />One code per customer</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />You set the credit for the customer and the friend</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />A monthly cap and an expiration date</ListItem>
                <ListItem fontSize="sm" color="gray.700"><ListIcon as={FiCheck} color="green.500" />A list of who referred the most orders</ListItem>
              </List>
            </Box>
          </SimpleGrid>

          {/* Additional marketing features row */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={8}>
            <Box p={6} borderRadius="xl" bg="white" border="1px solid" borderColor="gray.100" textAlign="center">
              <Icon as={FiMessageCircle} boxSize={8} color="blue.500" mb={3} />
              <Text fontWeight="700" mb={1}>Answers on your site</Text>
              <Text fontSize="sm" color="gray.600">The chat answers hours, prices, and whether you pick up their zip, from the hours and prices you entered. If they need a person, the message shows up for staff.</Text>
            </Box>
            <Box p={6} borderRadius="xl" bg="white" border="1px solid" borderColor="gray.100" textAlign="center">
              <Icon as={FiStar} boxSize={8} color="yellow.500" mb={3} />
              <Text fontWeight="700" mb={1}>A text asking for a Google review</Text>
              <Text fontSize="sm" color="gray.600">After the order is done, a text asks the customer for a Google review.</Text>
            </Box>
            <Box p={6} borderRadius="xl" bg="white" border="1px solid" borderColor="gray.100" textAlign="center">
              <Icon as={FiClock} boxSize={8} color="purple.500" mb={3} />
              <Text fontWeight="700" mb={1}>Send a text on a date</Text>
              <Text fontSize="sm" color="gray.600">Write a holiday text now. It goes out on the day you pick.</Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Why Smart Laundry Basket — benefit cards */}
      <Box id="why" py={{ base: 16, md: 20 }} bg="gray.50">
        <Container maxW="1200px">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Badge colorScheme="green" px={3} py={1} borderRadius="full">Why Smart Laundry Basket</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>Live the same day.</Heading>
          </VStack>

          {/* 4 key benefits, prominent */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mb={8}>
            {[
              { icon: FiClock, title: 'Live in 2 minutes', desc: 'Your name, prices, and hours. Then the counter and the customer site take orders.', color: 'blue' },
              { icon: FiDollarSign, title: 'Free under $1,000', desc: 'No charge until a month of wash and fold revenue passes $1,000. Then $49, never above $149.', color: 'green' },
              { icon: FiZap, title: 'Changes in about 48 hours', desc: 'Email a change. Most are done in about 48 hours, at no charge.', color: 'purple' },
              { icon: FiMail, title: 'Texts are included', desc: 'The ready text, a note when someone has not come in, a code for an unfinished pickup, and send-a-friend credit are in the monthly price.', color: 'pink' },
            ].map((b) => (
              <Box key={b.title} p={6} borderRadius="xl" bg="white" border="1px solid" borderColor="gray.100" textAlign="center"
                _hover={{ boxShadow: 'lg', transform: 'translateY(-4px)' }} transition="all 0.3s">
                <Box bg={`${b.color}.50`} w="52px" h="52px" borderRadius="lg" display="flex" alignItems="center" justifyContent="center" mx="auto" mb={3}>
                  <Icon as={b.icon} boxSize={6} color={`${b.color}.500`} />
                </Box>
                <Text fontWeight="800" fontSize="md" mb={1}>{b.title}</Text>
                <Text color="gray.600" fontSize="sm">{b.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>

          {/* Supporting "no-friction" points */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
            {[
              'No sales call to get started',
              'No credit card to start',
              'No contract',
              'Opens in Chrome on the counter tablet. Nothing to install.',
              'You set the prices, the minimum, and which neighborhoods you pick up',
              'Card payments go to your bank',
            ].map((point) => (
              <HStack key={point} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" px={4} py={3} spacing={3}>
                <Icon as={FiCheck} color="green.500" boxSize={5} flexShrink={0} />
                <Text fontSize="sm" fontWeight="600" color="gray.700">{point}</Text>
              </HStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <POSKitSection />

      {/* AI Garment Tracking — trust / accountability / fewer disputes */}
      <Box id="ai-tracking" py={{ base: 16, md: 20 }} bg="gray.900" color="white">
        <Container maxW="1100px">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Badge colorScheme="purple" px={3} py={1} borderRadius="full">Optional for both</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>A customer says a shirt is missing. Can you prove what came in?</Heading>
            <Text color="whiteAlpha.800" maxW="720px" fontSize="lg">
              Optional, for a store or a new wash and fold service. A picture is kept from before the wash and again at folding. <strong>1 cent per piece.</strong> If a shirt is missing, you have the picture.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={10}>
            <Box bg="whiteAlpha.100" borderRadius="2xl" p={7} border="1px solid" borderColor="whiteAlpha.300">
              <Icon as={FiShield} boxSize={7} color="purple.300" mb={3} />
              <Text fontWeight="700" fontSize="lg" mb={1}>A picture before the wash, and again at folding</Text>
              <Text fontSize="sm" color="whiteAlpha.800">That record is what the customer can trust. If a shirt is missing, you can show what came in.</Text>
            </Box>
            <Box bg="whiteAlpha.100" borderRadius="2xl" p={7} border="1px solid" borderColor="whiteAlpha.300">
              <Icon as={FiCheck} boxSize={7} color="green.300" mb={3} />
              <Text fontWeight="700" fontSize="lg" mb={1}>Staff keep their usual routine</Text>
              <Text fontSize="sm" color="whiteAlpha.800">No extra steps. Employees keep working the way they already do.</Text>
            </Box>
            <Box bg="whiteAlpha.100" borderRadius="2xl" p={7} border="1px solid" borderColor="whiteAlpha.300">
              <Icon as={FiClock} boxSize={7} color="blue.300" mb={3} />
              <Text fontWeight="700" fontSize="lg" mb={1}>Caught before it leaves</Text>
              <Text fontSize="sm" color="whiteAlpha.800">If what was folded does not match what came in, the employee is told before the order leaves the store.</Text>
            </Box>
          </SimpleGrid>

          <VStack spacing={3} mt={10}>
            <HStack spacing={3} flexWrap="wrap" justify="center">
              <Text fontSize="xs" fontWeight="700" color="purple.200" textTransform="uppercase" letterSpacing="wide">AI Garment Tracking</Text>
            </HStack>
            <Heading fontSize={{ base: '3xl', md: '4xl' }} fontWeight="900">1 cent / piece</Heading>
            <Text fontSize="sm" color="whiteAlpha.700" textAlign="center" maxW="560px">
              A store and a new wash and fold service both opt in here. We set it up on this screen, or on the laundry software you already run.
            </Text>
            <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
              <Button as="a" href="mailto:info@smartlaundrybasket.ai?subject=AI%20garment%20tracking%20for%20my%20laundromat" size="lg" colorScheme="purple" borderRadius="full" px={8}>I have a laundromat</Button>
              <Button as="a" href="mailto:info@smartlaundrybasket.ai?subject=AI%20garment%20tracking%20for%20my%20wash%20and%20fold%20service" size="lg" bg="white" color="purple.700" borderRadius="full" px={8} _hover={{ bg: 'gray.100' }}>I am starting a wash and fold service</Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* FAQ — SEO-friendly with semantic markup */}
      <Box as="section" id="faq" py={{ base: 16, md: 20 }} itemScope itemType="https://schema.org/FAQPage">
        <Container maxW="700px">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Badge colorScheme="gray" px={3} py={1} borderRadius="full">FAQ</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }}>Frequently Asked Questions</Heading>
            <Text color="gray.600" fontSize="md">The counter, the price, and optional AI garment tracking.</Text>
          </VStack>

          <Accordion allowMultiple>
            {FAQ_DATA.map((item, i) => (
              <AccordionItem key={i} border="none" mb={3} itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <AccordionButton bg="gray.50" borderRadius="xl" py={4} px={6} _hover={{ bg: 'gray.100' }} _expanded={{ bg: 'blue.50' }}>
                  <Text as="h3" flex="1" textAlign="left" fontWeight="600" fontSize="md" itemProp="name">{item.q}</Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel py={4} px={6} color="gray.600" fontSize="sm" lineHeight="tall"
                  itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <Text itemProp="text">{item.a}</Text>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box py={{ base: 16, md: 20 }} bg="blue.600" color="white" textAlign="center">
        <Container maxW="760px">
          <VStack spacing={6}>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>Wash and fold POS for your laundromat.</Heading>
            <Text fontSize="lg" opacity={0.9}>
              Software to start a wash and fold business for free. Then from $49. Never more than $149.
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button as="a" href="/quickstart" size="lg" bg="white" color="blue.600" borderRadius="full" px={8}
                _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }} boxShadow="lg">
                Set Up My Laundromat — Free
              </Button>
              <Button as="a" href="/quickstart" size="lg" bg="orange.400" color="white" borderRadius="full" px={8}
                _hover={{ bg: 'orange.500' }}>
                Start My Wash and Fold Business — Free
              </Button>
              <Button as="a" href="#ai-tracking"
                size="lg" variant="outline" borderColor="whiteAlpha.600" color="white" borderRadius="full" px={8}
                _hover={{ bg: 'whiteAlpha.200' }}>
                See AI garment tracking
              </Button>
            </HStack>

            {/* Scan-to-start QR — points to the onboarding URL */}
            <VStack spacing={2} pt={4}>
              <Box bg="white" p={2} borderRadius="lg" boxShadow="md">
                <Image
                  src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=https://www.smartlaundrybasket.ai/quickstart"
                  alt="Scan to set up your laundry at smartlaundrybasket.ai/quickstart"
                  boxSize="140px"
                />
              </Box>
              <Text fontSize="xs" opacity={0.85}>Scan to start · smartlaundrybasket.ai/quickstart</Text>
            </VStack>

            <Text fontSize="sm" opacity={0.85}>
              Questions? <Text as="a" href="mailto:info@smartlaundrybasket.ai" textDecoration="underline">info@smartlaundrybasket.ai</Text>
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box py={10} bg="gray.900" color="gray.400">
        <Container maxW="1200px">
          <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'center', md: 'flex-start' }} gap={8}>
            <VStack align={{ base: 'center', md: 'flex-start' }} spacing={2}>
              <Box bg="white" borderRadius="lg" px={3} py={2}>
                <Image src="/slb-logo.png" alt="Smart Laundry Basket" h="48px" objectFit="contain" />
              </Box>
              <Text fontSize="sm">Wash and fold software for a laundromat, and for an independent wash and fold service that picks up and delivers.</Text>
              <Text fontSize="xs">&copy; {new Date().getFullYear()} Smart Laundry Basket. All rights reserved.</Text>
            </VStack>
            <HStack spacing={8} fontSize="sm" flexWrap="wrap" align="flex-start" justify={{ base: 'center', md: 'flex-end' }} rowGap={6}>
              <VStack align="flex-start" spacing={2}>
                <Text fontWeight="bold" color="white">Product</Text>
                <Text as="a" href="#who-its-for" _hover={{ color: 'white' }}>Who this is for</Text>
                <Text as="a" href="#ai-tracking" _hover={{ color: 'white' }}>AI garment tracking</Text>
                <Text as="a" href="#tour" _hover={{ color: 'white' }}>Product Tour</Text>
                <Text as="a" href="#pricing" _hover={{ color: 'white' }}>Pricing</Text>
                <Text as="a" href="#pos-kit" _hover={{ color: 'white' }}>Counter kit</Text>
                <Text as="a" href="/updates" _hover={{ color: 'white' }}>Updates</Text>
                <Text as="a" href="/slb/get-customers" _hover={{ color: 'white' }}>Get customers</Text>
                <Text as="a" href="#faq" _hover={{ color: 'white' }}>FAQ</Text>
              </VStack>
              <VStack align="flex-start" spacing={2}>
                <Text fontWeight="bold" color="white">Compare</Text>
                <Text as="a" href="/cents-alternative" _hover={{ color: 'white' }}>Cents Alternative</Text>
                <Text as="a" href="/curbside-laundries-alternative" _hover={{ color: 'white' }}>Curbside Laundries Alternative</Text>
                <Text as="a" href="/laundromat-pos-software" _hover={{ color: 'white' }}>Laundromat software</Text>
                <Text as="a" href="/wdf-pos" _hover={{ color: 'white' }}>Wash-Dry-Fold POS</Text>
                <Text as="a" href="/llm-info" _hover={{ color: 'white' }}>Product facts</Text>
                <Text as="a" href="/wash-and-fold-pos" _hover={{ color: 'white' }}>Wash and fold software</Text>
                <Text as="a" href="/laundry-pickup-delivery-software" _hover={{ color: 'white' }}>Pickup & Delivery</Text>
                <Text as="a" href="/open-source-laundromat-pos" _hover={{ color: 'white' }}>Run it on your computer</Text>
              </VStack>
              <VStack align="flex-start" spacing={2}>
                <Text fontWeight="bold" color="white">Company</Text>
                <Text as="a" href="/quickstart" _hover={{ color: 'white' }}>Start free</Text>
                <Text as="a" href="https://calendar.app.google/Gu7fDZWRHYtrZK5H8" target="_blank" _hover={{ color: 'white' }}>Book Demo</Text>
                <Text as="button" onClick={openSource} _hover={{ color: 'white' }} textAlign="left">Run it yourself</Text>
                <Text as="a" href="mailto:info@smartlaundrybasket.ai" _hover={{ color: 'white' }}>Contact</Text>
                <Text as="a" href="/privacy-policy" _hover={{ color: 'white' }}>Privacy Policy</Text>
                <Text as="a" href="/terms" _hover={{ color: 'white' }}>Terms</Text>
              </VStack>
              <VStack align="flex-start" spacing={2}>
                <Text fontWeight="bold" color="white">Contact</Text>
                <Text fontSize="xs">900 E Palm Valley Blvd</Text>
                <Text fontSize="xs">Ste 1006-1007</Text>
                <Text fontSize="xs">Round Rock, TX 78664</Text>
                <Text as="a" href="tel:5125695939" fontSize="xs" _hover={{ color: 'white' }}>(512) 569-5939</Text>
                <Text as="a" href="mailto:info@smartlaundrybasket.ai" fontSize="xs" _hover={{ color: 'white' }}>info@smartlaundrybasket.ai</Text>
              </VStack>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* AI Chat Widget — for prospects to ask about the platform */}
      <ChatWidget
        customerId={visitorId}
        laundryId="platform"
        customerName="Website Visitor"
        customerPhone=""
      />

      {/* Request Source Access modal — shows the email + a prefilled message,
          with copy and "open email app" options so it works even without a
          configured mail client. */}
      <Modal isOpen={srcOpen} onClose={closeSource} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Run it on your own computer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Email us and we will send a copy you can run yourself, for your own laundry. Copy the address below, or open your email with the message filled in.
            </Text>
            <Text fontSize="sm" fontWeight="600" mb={1}>Email</Text>
            <InputGroup size="sm" mb={4}>
              <Input value={SOURCE_EMAIL} isReadOnly pr="4.5rem" />
              <InputRightElement width="4.5rem">
                <Button h="1.6rem" size="xs" onClick={copySourceEmail}>Copy</Button>
              </InputRightElement>
            </InputGroup>
            <Text fontSize="sm" fontWeight="600" mb={1}>Message</Text>
            <Textarea
              size="sm"
              rows={4}
              value={srcMessage}
              onChange={(e) => setSrcMessage(e.target.value)}
            />
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={closeSource}>Close</Button>
            <Button colorScheme="blue" onClick={openMailApp}>Open Email App</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProductWebsite;
